import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Typography
} from '@mui/material';
import Cropper, { Area } from 'react-easy-crop';

interface ImageCropDialogProps {
  /** Files queued for cropping; dialog shows one at a time */
  files: File[];
  open: boolean;
  /** Called with all cropped blobs once the queue is done */
  onComplete: (blobs: Blob[]) => void;
  onCancel: () => void;
}

const cropToBlob = (imageSrc: string, area: Area): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      // Cap canvas at 1600px — the server downscales to 1200 anyway
      const size = Math.min(area.width, 1600);
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not supported'));
        return;
      }
      ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, size, size);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Crop failed'))),
        'image/jpeg',
        0.92
      );
    };
    image.onerror = () => reject(new Error('Could not load image'));
    image.src = imageSrc;
  });

/**
 * Square (1:1) crop dialog matching the storefront's product image ratio.
 * Walks through multiple files one at a time.
 */
const ImageCropDialog: React.FC<ImageCropDialogProps> = ({ files, open, onComplete, onCancel }) => {
  const [index, setIndex] = useState(0);
  const [src, setSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [results, setResults] = useState<Blob[]>([]);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!open) {
      setIndex(0);
      setResults([]);
      return;
    }
    const file = files[index];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedArea(null);
    return () => URL.revokeObjectURL(url);
  }, [open, files, index]);

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedArea(areaPixels);
  }, []);

  const applyCrop = async () => {
    if (!src || !croppedArea) return;
    setWorking(true);
    try {
      const blob = await cropToBlob(src, croppedArea);
      const next = [...results, blob];
      if (index + 1 < files.length) {
        setResults(next);
        setIndex(index + 1);
      } else {
        onComplete(next);
      }
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        Crop image {files.length > 1 ? `(${index + 1} of ${files.length})` : ''}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Product photos display square — drag and zoom so the piece fills the frame.
        </Typography>
        <Box sx={{ position: 'relative', width: '100%', height: 340, bgcolor: '#111', borderRadius: 1 }}>
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Zoom
          </Typography>
          <Slider
            size="small"
            min={1}
            max={4}
            step={0.05}
            value={zoom}
            onChange={(_, v) => setZoom(v as number)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={applyCrop} disabled={working || !croppedArea}>
          {working ? 'Cropping…' : index + 1 < files.length ? 'Crop & next' : 'Crop & upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropDialog;
